import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useCommonComponentContext } from "@maysoft/common-component-react";

import useStrings from "../../useStrings";
import Helpers from "../../../commons/helpers";
import CommunityService from "../../../services/message/Community.service";
import { ICommunity, IMultiLanguageContent } from "../../../commons/interfaces";
import { CommunityType } from "../../../constants/enums";

export type ICommunityError = { [k in keyof ICommunity]?: string };

const useData = ({ id }: { id: string }) => {
    const theme = useTheme();
    const Strings = useStrings();

    const { onError = () => {}, onShowLoading = () => {}, onHideLoading = () => {}, onSuccess = () => {} } = useCommonComponentContext();

    const [data, setData] = useState<ICommunity>({
        type: CommunityType.Public,
        setting: {
            font: "Open Sans",
            primaryColor: theme.palette.primary.main,
        },
    } as ICommunity);

    const [error, setError] = useState<ICommunityError>({} as ICommunityError);

    useEffect(() => {
        getDetail(id);
    }, []);

    const getDetail = async (id: string) => {
        try {
            if (Helpers.isNullOrEmpty(id)) return;
            onShowLoading();
            const result = await CommunityService.getDetail(id);
            setData(result);
        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            onHideLoading();
        }
    };

    const handlePublish = () => {
        Helpers.showConfirmAlert(Strings.COMMUNITY.CONFIRM_PUBLISH, async () => {
            try {
                onShowLoading();
                await CommunityService.publish(data?.id);
                await CommunityService.getDetail(data?.id);
                onSuccess(Strings.COMMUNITY.PUBLISH_SUCCESS);
            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                onHideLoading();
            }
        });
    };

    const handleUnpublish = () => {
        Helpers.showConfirmAlert(Strings.COMMUNITY.CONFIRM_UNPUBLISH, async () => {
            try {
                onShowLoading();
                await CommunityService.unpublish(data?.id);
                await CommunityService.getDetail(data?.id);
                onSuccess(Strings.COMMUNITY.UNBLISH_SUCCESS);
            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                onHideLoading();
            }
        });
    };

    const onChangeValue = (value: string | IMultiLanguageContent, key: keyof ICommunity) => {
        if (!Helpers.isNullOrEmpty(error[key])) setError((prev) => ({ ...prev, [key]: "" }));
        // setData((prev) => ({ ...prev, [key]: value }));
        setData((prev) => ({ ...prev, [key]: value }));
    };

    const onChangeSetting = (value: string, key: keyof ICommunity["setting"]) => {
        setData((prev) => ({
            ...prev,
            setting: {
                ...prev.setting,
                [key]: value,
            },
        }));
    };

    return { data, error, setError, onChangeValue, getDetail, handlePublish, handleUnpublish, onChangeSetting };
};

export default useData;
